import { useSyncExternalStore, type ReactElement } from "react";
import { Divider, HudFrame, logical } from "../../ui/kit/index.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { HUD_SLOTS, PERF_PANEL, resolveSlot, type HudArea } from "../hud/slots.ts";
import type { PerfSnapshot } from "./monitor.ts";
import { PERF_WORDING, type PerfRow, type PerfView } from "./view.ts";

/**
 * The performance panel (ADR-087 decisions 2, 7 and 8): in its top-right slot, the kit's HUD frame holding the title
 * and the key that toggles it, the rows every player sees, for a staff viewer the counts of what the page holds, and,
 * once a fresh report has come, a divider and the server half's rows over the zone and area table, all in caption text. It reads the monitor's store, which changes twice a
 * second while the panel shows, and takes no pointer events. The component imports no stylesheet, so it renders to
 * static markup under `bun test`; `hud/mount.tsx` imports `perf.css`.
 */

/**
 * Where the panel reads: the monitor's store.
 */
export interface PerfSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => PerfSnapshot;
}

/**
 * One label and value row.
 *
 * @param props The `row`.
 * @returns The row.
 */
function Row({ row }: { readonly row: PerfRow }): ReactElement {
  return (
    <div className="ot-perf__row" data-perf-row={row.label}>
      <span className="ot-perf__label">{row.label}</span>
      <span className="ot-perf__value">{row.value}</span>
    </div>
  );
}

/**
 * The panel's content: the rows every player sees, then the staff rows and the server half when the view holds them.
 * A view without staff rows puts none of their labels or values on the page.
 *
 * @param props The `view`.
 * @returns The content.
 */
function Content({ view }: { readonly view: PerfView }): ReactElement {
  return (
    <>
      {view.client.map((row) => (
        <Row key={row.label} row={row} />
      ))}
      {view.staff === null ? null : (
        <div data-perf="staff">
          {view.staff.map((row) => (
            <Row key={row.label} row={row} />
          ))}
        </div>
      )}
      {view.server === null ? null : (
        <div data-perf="server">
          <Divider />
          <div className="ot-perf__heading">{PERF_WORDING.server}</div>
          {view.server.rows.map((row) => (
            <Row key={row.label} row={row} />
          ))}
          <div className="ot-perf__table" role="table" aria-label={PERF_WORDING.server}>
            <div className="ot-perf__cells ot-perf__cells--head" role="row">
              <span role="columnheader" />
              <span className="ot-perf__value" role="columnheader">
                {PERF_WORDING.zone}
              </span>
              <span className="ot-perf__value" role="columnheader">
                {PERF_WORDING.area}
              </span>
            </div>
            {view.server.table.map((row) => (
              <div key={row.label} className="ot-perf__cells" role="row" data-perf-row={row.label}>
                <span className="ot-perf__label" role="rowheader">
                  {row.label}
                </span>
                <span className="ot-perf__value" role="cell">
                  {row.zone}
                </span>
                <span className="ot-perf__value" role="cell">
                  {row.area}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The panel while it shows, nothing while it is hidden.
 *
 * @param props The `area` it is laid out on and the `source` it reads.
 * @returns The slot holding the panel, or null.
 */
export function PerfPanel({ area, source }: { readonly area: HudArea; readonly source: PerfSource }): ReactElement | null {
  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const key = useKeyLabel("performance");
  if (!snapshot.shown) {
    return null;
  }
  const slot = resolveSlot(HUD_SLOTS.perf, area);
  return (
    <div className="ot-hud-slot ot-perf" data-hud-slot="perf" role="status" aria-label={PERF_WORDING.title} style={{ left: logical(slot.left), top: logical(slot.top), width: logical(slot.width) }}>
      <HudFrame>
        <div className="ot-perf__body" style={{ width: logical(PERF_PANEL.width) }}>
          <div className="ot-perf__row ot-perf__title">
            <span>{PERF_WORDING.title}</span>
            <span className="ot-perf__key">{key}</span>
          </div>
          {snapshot.view === null ? null : <Content view={snapshot.view} />}
        </div>
      </HudFrame>
    </div>
  );
}
